import { Accessor, createMemo } from "solid-js";

import type {
  Completion,
  CompletionContext,
  CompletionResult,
} from "@codemirror/autocomplete";
import { startCompletion } from "@codemirror/autocomplete";
import { User } from "stoat.js";

import { useClient } from "@revolt/client";
import {
  UNICODE_EMOJI_PACK_PUA,
  unicodeEmojiUrl,
} from "@revolt/markdown/emoji/UnicodeEmoji";
import { GAME_TAGS, gameTagUrl } from "@revolt/markdown/plugins/gameTagData";
import { useState } from "@revolt/state";

import emojiMapping from "../../../emojiMapping.json";
import { AutoCompleteSearchSpace } from "../../utils/autoComplete";

import { isInCodeBlock } from "./codeMirrorCommon";

const EMOJI_KEYS = Object.keys(emojiMapping).sort();
const MAPPED_EMOJI_KEYS = EMOJI_KEYS.map(
  (id) =>
    ({
      type: "emoji",
      label: `:${id}:`,
      apply: emojiMapping[id as keyof typeof emojiMapping],
    }) as Completion,
);

const MASS_MENTIONS: Completion[] = [
  { type: "role", label: "@everyone", displayLabel: "@everyone" },
  { type: "role", label: "@here", displayLabel: "@here" },
  { type: "role", label: "@online", displayLabel: "@online" },
].map((entry) => ({ ...entry, apply: `${entry.label} ` }) as Completion);

/**
 * The two halves of a game tag.
 *
 * `@class` behaves like any other `@` completion, but what follows it is a
 * second step: the space that separates them ends the token the generic
 * pattern below can see, so the value needs a pattern that reaches back past
 * it. Everything after `@` is offered by kind first and by value second, which
 * is the only way a list of thirteen classes is ever short enough to read.
 */
const GAME_TAG_KINDS: Completion[] = ["class", "race"].map(
  (kind) =>
    ({
      type: "gameTag",
      label: `@${kind}`,
      displayLabel: `@${kind}`,
      // A function rather than a string, purely to reopen the list. Picking a
      // kind is half an answer, and inserting the text alone left the second
      // half waiting behind a space the user had to delete and type again
      // before anything would offer it.
      apply: (view, _completion, from, to) => {
        const insert = `@${kind} `;

        view.dispatch({
          changes: { from, to, insert },
          selection: { anchor: from + insert.length },
        });

        startCompletion(view);
      },
    }) as Completion,
);

const GAME_TAG_VALUES: Record<string, Completion[]> = {
  class: [],
  race: [],
};

for (const tag of GAME_TAGS) {
  GAME_TAG_VALUES[tag.kind].push({
    type: "gameTag",
    label: tag.slug,
    displayLabel: tag.label,
    apply: `${tag.slug} `,
    url: gameTagUrl(tag),
  } as Completion);
}

const RE_gameTagValue = /(?<!\w)@(class|race)[ \t]+[a-zA-Z-]*/i;
const RE_gameTagValueValidFor = /^[a-zA-Z-]*$/;

const RE_match = /(?<!\w)[:@%#]\w*/;
const RE_emojiValidFor = /(?<!\w):\w*/;
const RE_mentionValidFor = /(?<!\w)@\w*/;
const RE_roleValidFor = /(?<!\w)%\w*/;
const RE_channelValidFor = /(?<!\w)#\w*/;

export function codeMirrorAutoCompleteSource(
  searchSpace: Accessor<AutoCompleteSearchSpace>,
) {
  const state = useState();
  const client = useClient();

  const emoji = createMemo(() => {
    return ([] as Completion[]).concat(
      MAPPED_EMOJI_KEYS.map((emoji) => ({
        ...emoji,
        apply: `${UNICODE_EMOJI_PACK_PUA[state.settings.getValue("appearance:unicode_emoji")!] ?? ""}${emoji.apply as string}`,
        url: unicodeEmojiUrl(
          state.settings.getValue("appearance:unicode_emoji"),
          emoji.apply as string,
        ),
      })),
      client().emojis.map((emoji) => ({
        type: "emoji",
        label: `:${emoji.name}:`,
        apply: `:${emoji.id}: `,
        url: emoji.url,
      })),
    );
  });

  const users = createMemo(() =>
    (
      searchSpace()?.members ??
      searchSpace()?.users ??
      client().users.toList()
    ).map((entry) => {
      // avoiding using `instanceof`, presumed slow
      const user = ((entry as { user: User })?.user ?? entry) as User;

      return {
        type: "user",
        label: ("@" + entry.displayName).normalize("NFKC"),
        displayLabel: entry.displayName,
        detail:
          entry.displayName !== user.username
            ? `${user.username}#${user.discriminator}`
            : undefined,
        apply: `<@${typeof entry.id === "string" ? entry.id : entry.id.user}> `,
        url: entry.animatedAvatarURL,
      };
    }),
  );

  const roleCompletions = (prefix: string) =>
    searchSpace()?.roles?.map(
      (entry) =>
        ({
          type: "role",
          label: (prefix + entry.name).normalize("NFKC"),
          displayLabel: entry.name,
          apply: `<%${entry.id}> `,
          colour: entry.colour,
        }) as Completion,
    ) ?? [];

  const roles = createMemo(() =>
    searchSpace()?.canMentionRoles ? roleCompletions("%") : [],
  );

  const mentions = createMemo(() => {
    const space = searchSpace();

    return [
      ...(space?.canMentionEveryone ? MASS_MENTIONS : []),
      ...(space?.canMentionRoles ? roleCompletions("@") : []),
      ...GAME_TAG_KINDS,
      ...users(),
    ] as Completion[];
  });

  const channels = createMemo(() =>
    (searchSpace()?.channels ?? client().channels.toList()).map(
      (entry) =>
        ({
          type: "channel",
          label: ("#" + entry.name).normalize("NFKC"),
          displayLabel: "#" + entry.name,
          apply: `<#${entry.id}> `,
        }) as Completion,
    ),
  );

  // eslint-disable-next-line solid/reactivity
  return (context: CompletionContext) => {
    if (isInCodeBlock(context.state, context.pos, context.pos)) {
      return null;
    }

    // Before the generic token: at `@class pal` there is no `@…` token ending
    // at the cursor for it to find, so this would otherwise fall through to
    // nothing at exactly the point the value list is wanted.
    const tagValue = context.matchBefore(RE_gameTagValue);
    if (tagValue) {
      const parts = /^@(class|race)[ \t]+([a-zA-Z-]*)$/i.exec(tagValue.text);

      if (parts) {
        return {
          from: tagValue.to - parts[2].length,
          options: GAME_TAG_VALUES[parts[1].toLowerCase()],
          validFor: RE_gameTagValueValidFor,
        } as CompletionResult;
      }
    }

    const token = context.matchBefore(RE_match);
    if (!token) return null;
    const normalizedText = token.text.normalize("NFKC");
    switch (normalizedText[0]) {
      case ":":
        return {
          from: token.from,
          options: emoji(),
          validFor: RE_emojiValidFor,
        } as CompletionResult;
      case "@":
        return {
          from: token.from,
          options: mentions(),
          validFor: RE_mentionValidFor,
        } as CompletionResult;
      case "%":
        return {
          from: token.from,
          options: roles(),
          validFor: RE_roleValidFor,
        } as CompletionResult;
      case "#":
        return {
          from: token.from,
          options: channels(),
          validFor: RE_channelValidFor,
        } as CompletionResult;
      default:
        return null;
    }
  };
}
